/** Path prefix when game-client is proxied on the lobby host (mirrors casino-game-launcher). */
export const SAME_ORIGIN_GAME_CLIENT_PATH = '/game-client';

/** Standalone cross-origin distribution at gc.* (or *.gc.*). */
const STANDALONE_GC_HOST = /(^|\.)gc\./;

const SAME_ORIGIN_GAME_PLAY_REFERER = /\/game-play(\/|\?|#|$)/;

export const isLobbyEmbeddedQuery = (): boolean =>
  new URLSearchParams(window.location.search).get('embedded') === '1';

/**
 * Lobby embed: lobby (top) → launcher iframe (parent) → game-client iframe.
 * Redirect launch: lobby navigates away; launcher is top and direct parent of game-client.
 */
export const isLobbyEmbeddedStack = (): boolean => {
  try {
    if (window.parent !== window && window.top !== window.parent) {
      return true;
    }
  } catch {
    // Cross-origin access to top — fall through to query param.
  }

  return isLobbyEmbeddedQuery();
};

export const isSameOriginGameClientPath = (pathname: string): boolean =>
  Boolean(pathname) &&
  (pathname === SAME_ORIGIN_GAME_CLIENT_PATH ||
    pathname.startsWith(`${SAME_ORIGIN_GAME_CLIENT_PATH}/`));

export const isDevstackLobbyHost = (hostname: string): boolean =>
  hostname.includes('.devstack.') && !STANDALONE_GC_HOST.test(hostname);

export const isSameOriginLobbyLaunch = ({
  hostname = window.location.hostname,
  pathname = window.location.pathname,
  referrer = document.referrer,
}: {
  hostname?: string;
  pathname?: string;
  referrer?: string;
} = {}): boolean => {
  if (isLobbyEmbeddedStack() || isLobbyEmbeddedQuery()) {
    return true;
  }

  if (isSameOriginGameClientPath(pathname)) {
    return true;
  }

  if (isDevstackLobbyHost(hostname)) {
    return true;
  }

  return SAME_ORIGIN_GAME_PLAY_REFERER.test(referrer);
};

/**
 * Window that should receive GC/* proxy messages.
 * Always the immediate parent (launcher). Never top when lobby is still in the stack.
 */
export const getLauncherPostMessageTarget = (): Window | null => {
  if (window.parent !== window) {
    return window.parent;
  }

  // Redirect / popup: launcher document is top-level; parent === top.
  return window.top ?? window;
};

/**
 * Target origin for postMessage to the launcher.
 * - Lobby embed + same-origin paths: shared lobby host origin (no gc→launcher remap).
 * - Redirect / gc.* subdomain: hostname mapping to launcher.* (legacy mobile web).
 */
export const resolveLauncherPostMessageOrigin = ({
  hostname = window.location.hostname,
  pathname = window.location.pathname,
  origin = window.location.origin,
  referrer = document.referrer,
}: {
  hostname?: string;
  pathname?: string;
  origin?: string;
  referrer?: string;
} = {}): string => {
  if (
    isLobbyEmbeddedStack() ||
    isSameOriginLobbyLaunch({ hostname, pathname, referrer })
  ) {
    try {
      if (window.parent !== window) {
        return window.parent.location.origin;
      }
    } catch {
      // Same-origin embed fallback.
    }

    return origin.replace(':5173', '');
  }

  try {
    if (window.parent !== window) {
      return window.parent.location.origin;
    }
  } catch {
    // Cross-origin parent (gc.* iframe under launcher.*).
  }

  let windowURL: string;
  if (referrer.includes('https://gw') || referrer.includes('.gw.devstack')) {
    windowURL = `https://${hostname.replace('gc', 'gw')}`;
  } else {
    windowURL = `https://${hostname.replace('gc', 'launcher')}`;
  }

  if (process.env?.VITE_HOST) {
    windowURL = `${windowURL}:3000`;
  }

  return windowURL;
};
